import React, { useState } from "react";
import { useT } from "../lib/i18n";
import { Info, X, Database, Shield, Clock } from "@phosphor-icons/react";
import TrustBadge from "./TrustBadge";
import SocialProofCarousel from "./SocialProofCarousel";
import CardsInFlight from "./CardsInFlight";
import LivePulseRail from "./LivePulseRail";

/**
 * Unified dark-themed "trust strip" — visually stitches the three live
 * landing-page widgets (TrustBadge, SocialProofCarousel, CardsInFlight)
 * into one continuous section sharing a dark-green background, soft
 * glass-morphism dividers, and a single header with a "How we collect this"
 * trigger that opens TrustSourcesModal.
 *
 * Each child widget hides itself when it has no data — if all three are
 * empty the strip renders nothing at all.
 */
export default function TrustStrip() {
    const t = useT();
    const [open, setOpen] = useState(false);

    return (
        <section
            className="bg-[#0A1F16] text-[#FBF9F6] border-t border-white/10"
            data-testid="trust-strip"
        >
            <div className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <p className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                            {t("trust_strip.eyebrow")}
                        </p>
                        <h2 className="serif text-3xl sm:text-5xl mt-2 leading-tight max-w-xl">
                            <span className="text-[#FBF9F6]">{t("trust_strip.title_a")}</span>{" "}
                            <span className="italic text-[#CBA35C]">{t("trust_strip.title_b")}</span>
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-white/70 hover:text-white px-3 py-1.5 rounded-full border border-white/15 hover:border-white/30 backdrop-blur-md bg-white/5 transition-colors"
                        data-testid="trust-strip-how-trigger"
                    >
                        <Info size={14} weight="duotone" /> {t("trust_strip.how_link")}
                    </button>
                </div>

                {/* Stitched widgets — each in its own backdrop-blurred card,
                    separated by a subtle glass divider. The vertical pulse
                    rail lives outside the stitched stack so it spans the
                    whole strip's height on desktop. */}
                <div className="flex gap-6 items-stretch">
                    <div className="flex-1 space-y-px rounded-3xl overflow-hidden border border-white/10 bg-white/[0.03] backdrop-blur-md">
                        <div className="px-6 sm:px-8 py-8 bg-white/[0.02]">
                            <SocialProofCarousel theme="dark" standalone={false} />
                        </div>
                        <div className="px-6 sm:px-8 py-8 bg-white/[0.02]">
                            <CardsInFlight theme="dark" standalone={false} />
                        </div>
                        <div className="px-6 sm:px-8 py-10 bg-white/[0.02]">
                            <TrustBadge theme="dark" standalone={false} />
                        </div>
                    </div>
                    <LivePulseRail />
                </div>
            </div>

            {open && <TrustSourcesModal onClose={() => setOpen(false)} />}
        </section>
    );
}

function TrustSourcesModal({ onClose }) {
    const t = useT();
    return (
        <div
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={onClose}
            data-testid="trust-sources-modal"
        >
            <div
                className="w-full max-w-xl bg-[#FBF9F6] text-[#112318] rounded-3xl p-7 shadow-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A5C50]">
                            {t("trust_strip.modal_eyebrow")}
                        </p>
                        <h3 className="serif text-2xl mt-1.5">{t("trust_strip.modal_title")}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 p-1.5 rounded-full hover:bg-black/5 transition-colors"
                        aria-label={t("trust_strip.modal_close")}
                        data-testid="trust-sources-modal-close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <p className="mt-3 text-sm text-[#4A5C50] leading-relaxed">
                    {t("trust_strip.modal_intro")}
                </p>

                <ul className="mt-5 space-y-5">
                    <Source
                        Icon={Database}
                        title={t("trust_strip.src_carousel_title")}
                        body={t("trust_strip.src_carousel_body")}
                    />
                    <Source
                        Icon={Shield}
                        title={t("trust_strip.src_flight_title")}
                        body={t("trust_strip.src_flight_body")}
                    />
                    <Source
                        Icon={Clock}
                        title={t("trust_strip.src_badges_title")}
                        body={t("trust_strip.src_badges_body")}
                    />
                </ul>

                <div className="mt-6 rounded-xl bg-[#184A30]/5 border border-[#184A30]/15 p-4 text-[13px] text-[#184A30]">
                    {t("trust_strip.modal_promise")}
                </div>
            </div>
        </div>
    );
}

function Source({ Icon, title, body }) {
    return (
        <li className="flex gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-[#184A30] text-[#FBF9F6] flex items-center justify-center">
                <Icon size={18} weight="duotone" />
            </div>
            <div className="flex-1">
                <p className="font-semibold text-sm">{title}</p>
                <p className="mt-1 text-[13px] text-[#4A5C50] leading-relaxed">{body}</p>
            </div>
        </li>
    );
}
